import { useEffect, useState, type CSSProperties } from "react";
import type { GiftCardThemeSnapshot } from "@booking-seconds/types";
import { formatCurrency } from "@booking-seconds/utils";

const fallbackTheme: GiftCardThemeSnapshot = {
  id: "",
  name: "Just Because",
  slug: "just-because",
  occasionType: "general",
  backgroundImageUrl: "",
  accentColor: "#c65f3f",
  textColor: "#18261f",
  headline: "A thoughtful gift for you",
  subheadline: "Use this gift card whenever you're ready."
};

function normalizeTheme(theme?: Partial<GiftCardThemeSnapshot> | null): GiftCardThemeSnapshot {
  return {
    id: String(theme?.id ?? fallbackTheme.id),
    name: String(theme?.name ?? fallbackTheme.name),
    slug: String(theme?.slug ?? fallbackTheme.slug),
    occasionType: String(theme?.occasionType ?? fallbackTheme.occasionType),
    backgroundImageUrl: String(theme?.backgroundImageUrl ?? fallbackTheme.backgroundImageUrl),
    accentColor: String(theme?.accentColor ?? fallbackTheme.accentColor),
    textColor: String(theme?.textColor ?? fallbackTheme.textColor),
    headline: String(theme?.headline ?? fallbackTheme.headline),
    subheadline: String(theme?.subheadline ?? fallbackTheme.subheadline)
  };
}

export function GiftCardThemePreview(props: {
  theme?: Partial<GiftCardThemeSnapshot> | null;
  amount: number;
  recipientName?: string;
  purchaserName?: string;
  message?: string;
  code?: string;
  caption?: string;
}) {
  const theme = normalizeTheme(props.theme);
  const [backgroundFailed, setBackgroundFailed] = useState(false);
  const normalizedBackgroundUrl = String(theme.backgroundImageUrl ?? "").trim();
  const showBackgroundImage = Boolean(normalizedBackgroundUrl) && !backgroundFailed;

  useEffect(() => {
    setBackgroundFailed(false);
  }, [normalizedBackgroundUrl]);

  const previewStyle: CSSProperties = {
    color: theme.textColor,
    backgroundColor: theme.accentColor,
    backgroundImage: showBackgroundImage
      ? `linear-gradient(145deg, rgba(255,255,255,0.08), rgba(21,31,27,0.18))`
      : `linear-gradient(145deg, ${theme.accentColor}, rgba(246, 240, 230, 0.92))`
  };

  return (
    <div className="gift-card-preview" style={previewStyle}>
      {showBackgroundImage ? (
        <img
          className="gift-card-preview__image"
          src={normalizedBackgroundUrl}
          alt=""
          aria-hidden="true"
          onError={() => setBackgroundFailed(true)}
        />
      ) : null}
      <div className="gift-card-preview__overlay">
        <div className="gift-card-preview__topline">
          <div>
            <p className="gift-card-preview__occasion">{theme.name}</p>
            <h3>{theme.headline}</h3>
            <p>{theme.subheadline}</p>
          </div>
          <div className="gift-card-preview__amount">{formatCurrency(Number(props.amount ?? 0))}</div>
        </div>

        <div className="gift-card-preview__meta">
          <div>
            <span>To</span>
            <strong>{props.recipientName || "Someone special"}</strong>
          </div>
          <div>
            <span>From</span>
            <strong>{props.purchaserName || "Someone"}</strong>
          </div>
          {props.code ? (
            <div>
              <span>Code</span>
              <strong>{props.code}</strong>
            </div>
          ) : null}
        </div>

        {props.message ? (
          <div className="gift-card-preview__message">
            <span>Message</span>
            <p>{props.message}</p>
          </div>
        ) : null}

        {props.caption ? <p className="gift-card-preview__caption">{props.caption}</p> : null}
      </div>
    </div>
  );
}
